import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

const LandingPage: React.FC = () => {
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    const features = [
        {
            icon: '📸',
            title: 'AI-Powered Listings',
            description: 'Take a photo of your product and watch as our AI generates professional titles, descriptions, tags, and categories in seconds.',
            highlight: 'Save 2+ hours per listing',
            available: true
        },
        {
            icon: '📦',
            title: 'Smart Inventory',
            description: 'Scan delivery notes with your camera to automatically update stock levels. Track raw materials, finished products, and everything in between.',
            highlight: 'Real-time sync',
            available: true
        },
        {
            icon: '🎭',
            title: '3D Model Generation',
            description: 'Transform your product photos into stunning 3D models. Perfect for showcasing your creations from every angle.',
            highlight: 'Powered by AI',
            available: true
        },
        {
            icon: '🧱',
            title: 'Raw Materials Tracking',
            description: 'Keep track of clay, wood, metal, fabric and all your supplies. Know exactly what you have and when to reorder.',
            highlight: 'Never run out',
            available: true
        },
        {
            icon: '📄',
            title: 'Delivery Note Scanner',
            description: 'Photograph your supplier delivery notes and let AI extract and update your inventory automatically.',
            highlight: 'Paperwork automated',
            available: true
        },
        {
            icon: '🔗',
            title: 'Shopify Integration',
            description: 'Connect your Shopify store and manage products, sync inventory, and streamline your workflow.',
            highlight: 'Seamless sync',
            available: true
        }
    ];

    const comingSoonFeatures = [
        {
            icon: '🔄',
            title: 'Multichannel Sync',
            description: 'One-click listing and inventory sync across Amazon, Etsy, eBay, and more. Manage everything from one dashboard.',
        },
        {
            icon: '🤖',
            title: 'AI Customer Service',
            description: '24/7 AI assistant that handles customer inquiries, answers product questions, and provides order updates.',
        },
        {
            icon: '✨',
            title: 'AR Shopping Experience',
            description: 'Let customers see your products in their space before buying with augmented reality.',
        }
    ];

    const howItWorks = [
        {
            step: '01',
            title: 'Sign Up Free',
            description: 'Create your account in seconds. No credit card required to get started.',
            image: '/screenshots/1_login.png'
        },
        {
            step: '02',
            title: 'AI Listing Creation',
            description: 'Use your phone to capture your creations. Our AI handles the rest—descriptions, tags, pricing suggestions.',
            image: '/screenshots/3_create.png'
        },
        {
            step: '03',
            title: 'Unified Dashboard',
            description: 'Get a clear overview of your business—orders, revenue, and inventory sync in one place.',
            image: '/screenshots/2_home.png'
        },
        {
            step: '04',
            title: 'AI Command Center',
            description: 'Manage all your platforms and get AI-powered insights to grow your artisan business.',
            image: '/screenshots/4_command.png'
        }
    ];

    const faqs = [
        {
            question: 'What makes Commerium different from other e-commerce tools?',
            answer: 'Commerium is built specifically for artisans and makers. We understand that handmade products need unique descriptions, careful inventory management of raw materials, and personal touches. Our AI is trained to understand craft terminology and artisan workflows.'
        },
        {
            question: 'How does the AI listing generation work?',
            answer: 'Simply photograph your product from one or more angles. Our AI analyzes the images to identify materials, style, and craftsmanship details. It then generates SEO-optimized titles, descriptions, and tags tailored for your marketplace.'
        },
        {
            question: 'Is there a free plan?',
            answer: 'Yes! Our free plan includes 10 AI-generated listings per month, basic inventory tracking, and 1 marketplace connection. Perfect for trying out the platform.'
        },
        {
            question: 'How does the 3D model generation work?',
            answer: 'Upload photos of your product and our AI creates a 3D model you can view from any angle. Great for giving customers a better understanding of your handmade items.'
        },
        {
            question: 'What marketplaces can I connect?',
            answer: 'Currently we support Shopify. We are actively working on integrations for Etsy, Amazon Handmade, eBay, and WooCommerce—coming soon!'
        },
        {
            question: 'Can I cancel anytime?',
            answer: 'Absolutely. There are no long-term contracts. You can upgrade, downgrade, or cancel your subscription at any time from your dashboard.'
        }
    ];

    const pricing = [
        {
            name: 'Free',
            price: '0',
            period: '/month',
            description: 'Try Commerium and see if it works for you',
            features: [
                '10 AI text listings',
                '1 marketplace connection',
                '100 products max',
                'Community support'
            ],
            cta: 'Get Started Free',
            link: 'https://app.trycommerium.com',
            popular: false
        },
        {
            name: 'artisan',
            price: '15',
            period: '/month',
            description: 'or $150/year. 3-day trial. For hobbyists & new sellers',
            features: [
                '30 Photo to listings',
                'Unlimited Stock Scan',
                'Unlimited Raw Materials Scan',
                'AI Scout'
            ],
            cta: 'Get Started',
            link: 'https://app.trycommerium.com',
            popular: false
        },
        {
            name: 'studio',
            price: '29',
            period: '/month',
            description: 'or $290/year. 1-day trial. For growing artisan businesses',
            features: [
                '50 listings',
                'Unlimited Stock Scan',
                'Unlimited Raw Materials Scan',
                'AI Scout'
            ],
            cta: 'Get Started',
            link: 'https://app.trycommerium.com',
            popular: true
        },
        {
            name: 'scale',
            price: '57',
            period: '/month',
            description: 'or $570/year. 1-day trial. For studios & established workshops',
            features: [
                '100 Listings',
                'Unlimited Stock Scan',
                'Unlimited Raw Materials Scan',
                'AI Scout'
            ],
            cta: 'Get Started',
            link: 'https://app.trycommerium.com',
            popular: false
        }
    ];

    return (
        <div className="landing-page">
            {/* Presale Banner */}
            <div className="presale-banner">
                <span className="banner-icon">🎨</span>
                <p><strong>Early Access:</strong> Commerium is in review. Lock in founder pricing before the public launch.</p>
            </div>

            {/* Navigation */}
            <nav className="navbar">
                <Link to="/" className="logo">Commerium</Link>
                <div className="nav-links">
                    <a href="#features" className="nav-link">Features</a>
                    <a href="#how-it-works" className="nav-link">How It Works</a>
                    <a href="#demo" className="nav-link">Demo</a>
                    <a href="#pricing" className="nav-link">Pricing</a>
                    <a href="#faq" className="nav-link">FAQ</a>
                    <a href="https://app.trycommerium.com" className="btn-secondary">Log In</a>
                    <a href="https://app.trycommerium.com" className="btn-primary">Get Started Free</a>
                </div>
                <button className="mobile-menu-btn">☰</button>
            </nav>

            {/* Hero Section */}
            <section className="hero">
                <div className="hero-content">
                    <div className="hero-status">
                        <span className="status-badge pulse"></span>
                        <span className="status-text">Early Access — Now Live</span>
                    </div>
                    <h1>Your craft deserves an <span className="gradient-text">audience.</span> We handle the <span className="gradient-text">rest.</span></h1>
                    <p>
                        Photograph your products. Let AI write the listings, track your inventory,
                        and manage your Shopify store—so you can spend more time making and less time
                        on everything else.
                    </p>
                    <div className="hero-btns">
                        <a href="https://app.trycommerium.com" className="btn-primary btn-large">
                            Start for Free
                            <span className="btn-arrow">→</span>
                        </a>
                        <a href="#demo" className="btn-outline">
                            Watch Demo
                        </a>
                    </div>
                    <div className="hero-trust">
                        <span>✓ No credit card required</span>
                        <span>✓ Free plan forever</span>
                        <span>✓ Cancel anytime</span>
                    </div>
                </div>
                <div className="hero-visual">
                    <div className="floating-card card-1">
                        <span className="floating-icon">✨</span>
                        <span>AI Listing Tools</span>
                    </div>
                    <div className="hero-screenshot">
                        <div className="screenshot-browser-bar">
                            <span className="dot dot-1"></span>
                            <span className="dot dot-2"></span>
                            <span className="dot dot-3"></span>
                        </div>
                        <img src="/assets/dashboard.png" alt="Commerium Dashboard" className="main-screenshot" />
                    </div>
                    <div className="floating-card card-2">
                        <span className="floating-icon">📦</span>
                        <span>Shopify Sync</span>
                    </div>
                </div>
            </section>

            {/* Story Section 1: The Craft */}
            <section className="story-section the-craft">
                <div className="story-badge">The Craft</div>
                <h2 className="story-title">
                    Why did you start <span className="gradient-text">creating?</span>
                </h2>
                <p className="story-subtitle">For the art? Or the spreadsheets?</p>
            </section>

            {/* Story Section 2: The Reality */}
            <section className="story-section the-reality">
                <div className="story-badge">The Reality</div>
                <h2 className="story-title">
                    Building a business <span className="gradient-text">takes you away</span> from making.
                </h2>
                <div className="chaos-overlay">
                    <span className="chaos-word" style={{ top: '10%', left: '5%' }}>Listings</span>
                    <span className="chaos-word" style={{ top: '20%', right: '10%' }}>Inventory?</span>
                    <span className="chaos-word" style={{ bottom: '15%', left: '15%' }}>SEO Tags</span>
                    <span className="chaos-word" style={{ bottom: '25%', right: '20%' }}>Invoices</span>
                </div>
            </section>

            {/* Story Section 3: The Solution / Demo */}
            <section id="demo" className="story-section the-solution">
                <div className="story-badge">The Solution</div>
                <h2 className="story-title">
                    Meet <span className="gradient-text">Commerium.</span>
                </h2>
                <p className="story-subtitle">Watch the full demo of how we handle the business side for you.</p>
                
                <div className="demo-fullscreen-container">
                    <div className="video-wrapper">
                        <video 
                            className="demo-video"
                            controls
                            poster="/screenshots/2_home.png"
                        >
                            <source src="/videos/commerium-demo.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="features">
                <div className="section-header">
                    <span className="section-badge">Features</span>
                    <h2>Everything You Need to <span className="gradient-text">Create & Sell</span></h2>
                    <p>Built by makers, for makers. Tools that actually understand your workflow.</p>
                </div>

                <div className="features-grid">
                    {features.map((feature, index) => (
                        <div key={index} className="feature-card">
                            <span className="feature-icon">{feature.icon}</span>
                            <h3>{feature.title}</h3>
                            <p>{feature.description}</p>
                            <span className="feature-highlight">{feature.highlight}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Coming Soon Section */}
            <section className="coming-soon-section">
                <div className="section-header">
                    <span className="section-badge coming-soon-badge">Coming Soon</span>
                    <h2>What's <span className="gradient-text">Next</span></h2>
                    <p>We're actively building these features. Join now to get early access.</p>
                </div>

                <div className="coming-soon-grid">
                    {comingSoonFeatures.map((feature, index) => (
                        <div key={index} className="coming-soon-card">
                            <span className="coming-soon-icon">{feature.icon}</span>
                            <h3>{feature.title}</h3>
                            <p>{feature.description}</p>
                            <span className="coming-soon-label">Coming Soon</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* How It Works Section */}
            <section id="how-it-works" className="how-it-works">
                <div className="section-header">
                    <span className="section-badge">How It Works</span>
                    <h2>From Photo to Listing in <span className="gradient-text">Minutes</span></h2>
                    <p>Getting started is simple. Here's how Commerium transforms your workflow.</p>
                </div>

                <div className="steps-container">
                    {howItWorks.map((step, index) => (
                        <div key={index} className="step-wrapper">
                            <div className={`step-card ${index % 2 === 1 ? 'reverse' : ''}`}>
                                <div className="step-text">
                                    <div className="step-number">{step.step}</div>
                                    <h3>{step.title}</h3>
                                    <p>{step.description}</p>
                                </div>
                                <div className="step-visual">
                                    <div className="step-screenshot-container">
                                        <img src={step.image} alt={step.title} className="step-screenshot" />
                                        <div className="screenshot-shadow"></div>
                                    </div>
                                </div>
                            </div>
                            {index < howItWorks.length - 1 && <div className="step-divider"></div>}
                        </div>
                    ))}
                </div>
            </section>


            {/* AI Image Magic Showcase */}
            <section className="image-magic">
                <div className="section-header">
                    <span className="section-badge">AI Image Magic</span>
                    <h2>Transform One Photo into a <span className="gradient-text">Complete Gallery</span></h2>
                    <p>Our AI doesn't just see your product—it understands it. Generate lifestyles, tech specs, and artistic shots in any measurement system.</p>
                </div>

                <div className="image-gallery-container">
                    <div className="gallery-main">
                        <div className="gallery-item base">
                            <img src="/assets/ai/artisan_stool_workshop_final.png" alt="Mundane Workshop Stool" />
                            <span className="image-label">Your Original Photo</span>
                        </div>
                        <div className="gallery-arrow">→</div>
                        <div className="gallery-display">
                            <div className="display-grid">
                                <div className="gallery-item">
                                    <img src="/assets/ai/artisan_stool_fixed_lifestyle_1768912676134.png" alt="Lifestyle" />
                                    <span className="image-label">Lifestyle Enhancement</span>
                                </div>
                                <div className="gallery-item">
                                    <img src="/assets/ai/artisan_stool_fixed_cm_1768912710442.png" alt="Metric Specs (cm)" />
                                    <span className="image-label">Metric Specs (cm)</span>
                                </div>
                                <div className="gallery-item">
                                    <img src="/assets/ai/artisan_stool_fixed_in_1768912729304.png" alt="Imperial Specs (in)" />
                                    <span className="image-label">Imperial Specs (in)</span>
                                </div>
                                <div className="gallery-item">
                                    <img src="/assets/ai/artisan_stool_fixed_bw_1768912694488.png" alt="Black & White Artistic" />
                                    <span className="image-label">Black & White Studio</span>
                                </div>
                                <div className="gallery-item">
                                    <img src="/assets/ai/artisan_stool_clean_final.jpg" alt="Amazon Compliant" />
                                    <span className="image-label">Amazon Compliant</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="feature-info-bar">
                    <div className="info-item">
                        <span className="info-icon">⚖️</span>
                        <div>
                            <h4>Smart Weight & Specs</h4>
                            <p>AI automatically estimates weight (4.2kg / 9.3 lbs) and technical requirements.</p>
                        </div>
                    </div>
                    <div className="info-item">
                        <span className="info-icon">📏</span>
                        <div>
                            <h4>Any Unit System</h4>
                            <p>Switch between CM, IN, or even custom industry standards with one click.</p>
                        </div>
                    </div>
                    <div className="info-item">
                        <span className="info-icon">✨</span>
                        <div>
                            <h4>Studio Quality</h4>
                            <p>Perfect lighting, background removal, and professional color grading automatically applied.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Early Access Section (instead of fake testimonials) */}
            <section className="early-access">
                <div className="section-header">
                    <span className="section-badge">Early Access</span>
                    <h2>Be Part of the <span className="gradient-text">Journey</span></h2>
                    <p>We're building Commerium with feedback from real artisans. Join early and help shape the future.</p>
                </div>

                <div className="early-access-content">
                    <div className="early-access-card">
                        <div className="early-icon">🎨</div>
                        <h3>Built for Artisans</h3>
                        <p>Every feature is designed with makers in mind—from ceramicists to woodworkers to jewelry designers.</p>
                    </div>
                    <div className="early-access-card">
                        <div className="early-icon">💬</div>
                        <h3>Your Feedback Matters</h3>
                        <p>Early users directly influence what we build next. Tell us what you need.</p>
                    </div>
                    <div className="early-access-card">
                        <div className="early-icon">🎁</div>
                        <h3>Early Adopter Perks</h3>
                        <p>Get lifetime discounts and first access to new features as we grow together.</p>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="pricing">
                <div className="section-header">
                    <span className="section-badge">Pricing</span>
                    <h2>Simple, <span className="gradient-text">Honest</span> Pricing</h2>
                    <p>Start free. Upgrade when you need to. No hidden fees.</p>
                </div>

                <div className="pricing-grid">
                    {pricing.map((plan, index) => (
                        <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                            {plan.popular && <span className="popular-badge">Most Popular</span>}
                            <h3>{plan.name}</h3>
                            <div className="price">
                                <span className="currency">$</span>
                                <span className="amount">{plan.price}</span>
                                <span className="period">{plan.period}</span>
                            </div>
                            <p className="plan-description">{plan.description}</p>
                            <ul className="plan-features">
                                {plan.features.map((feature, i) => (
                                    <li key={i}>✓ {feature}</li>
                                ))}
                            </ul>
                            <a href={plan.link} className={plan.popular ? 'btn-primary' : 'btn-outline'}>
                                {plan.cta}
                            </a>
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className="faq">
                <div className="section-header">
                    <span className="section-badge">FAQ</span>
                    <h2>Frequently Asked <span className="gradient-text">Questions</span></h2>
                    <p>Everything you need to know about Commerium.</p>
                </div>

                <div className="faq-container">
                    {faqs.map((faq, index) => (
                        <div
                            key={index}
                            className={`faq-item ${openFaq === index ? 'open' : ''}`}
                            onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        >
                            <div className="faq-question">
                                <span>{faq.question}</span>
                                <span className="faq-toggle">{openFaq === index ? '−' : '+'}</span>
                            </div>
                            {openFaq === index && (
                                <div className="faq-answer">{faq.answer}</div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="cta-card">
                    <h2>Built for makers.<br /><span className="gradient-text">Ready when you are.</span></h2>
                    <p>Stop losing time on listings, spreadsheets, and inventory chaos. Let Commerium handle the business side—so you can focus on the craft that started it all.</p>
                    <div className="cta-btns">
                        <a href="https://app.trycommerium.com" className="btn-primary btn-large">
                            Start for Free
                            <span className="btn-arrow">→</span>
                        </a>
                    </div>
                    <span className="cta-note">Free plan forever · No credit card · Cancel anytime</span>
                </div>
            </section>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-main">
                    <div className="footer-brand">
                        <span className="footer-logo">Commerium</span>
                        <p>AI-powered e-commerce for artisans. Create, manage, and grow your handmade business.</p>
                        <div className="footer-social">
                            <a href="#" aria-label="Twitter">𝕏</a>
                            <a href="#" aria-label="Instagram">📷</a>
                        </div>
                    </div>
                    <div className="footer-links">
                        <div className="footer-column">
                            <h4>Product</h4>
                            <a href="#features">Features</a>
                            <a href="#pricing">Pricing</a>
                            <a href="#how-it-works">How It Works</a>
                            <a href="#faq">FAQ</a>
                        </div>
                        <div className="footer-column">
                            <h4>Company</h4>
                            <a href="mailto:hello@trycommerium.com">Contact</a>
                        </div>
                        <div className="footer-column">
                            <h4>Legal</h4>
                            <Link to="/privacy">Privacy Policy</Link>
                            <Link to="/terms">Terms of Service</Link>
                            <Link to="/cookies">Cookie Policy</Link>
                            <Link to="/gdpr">GDPR Compliance</Link>
                        </div>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p>© 2026 Commerium. All rights reserved. (v1.6)</p>
                    <p>Made with ❤️ for artisans</p>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
